import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import type { InternalMeetingConfigProblem } from '@ai-meeting/shared'
import { ArchiveAudioRecorder } from '@/audio/ArchiveAudioRecorder'
import { ChevronLeftIcon, MicIcon, StopIcon } from '@/components/Icons'
import { useInternalMeetingRecording } from '@/hooks/useInternalMeetingRecording'
import { MEDIA_LG_UP, useMediaQuery } from '@/hooks/useMediaQuery'
import { useOnlineStatus } from '@/hooks/useOnlineStatus'
import { useUiStrings } from '@/hooks/useUiStrings'
import { useStore } from '@/store/useStore'
import { getCurrentUserId, getCurrentUserName } from '@/services/session'
import { formatElapsedHms } from '@/utils/formatElapsedHms'
import { serializeSttConfigContextForMinutesReference } from '@/utils/sttConfigContext'
import { getLanguageDisplayName } from '@/utils/languageDisplayName'
import {
  completeMeetingMetadata,
  requestMeetingMinutesFromAudioInBackground,
  uploadMeetingAudioInBackground,
} from '@/pages/Meetings/utils/saveCompletedMeetingInBackground'
import { registerInternalMeetingCaptureStream } from '@/pages/Meetings/utils/internalMeetingCaptureBridge'
import { ChromeHideButton, ChromeRestoreStrip, CollapsibleChrome } from './MeetingMobileChrome'
import { LiveAudioLevelBars } from './LiveAudioLevelBars'
import {
  meetingSessionStatusTone,
  type MeetingSessionTone,
} from './meeting-session-status'

const STATUS_PILL_CLASS: Record<MeetingSessionTone, string> = {
  live: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300',
  pending:
    'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300',
  fault:
    'border-red-300 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300',
  idle: 'border-slate-200 bg-slate-50 text-slate-600 dark:border-[var(--border-color)] dark:bg-white/5 dark:text-[var(--text-secondary)]',
}

const STATUS_DOT_CLASS: Record<MeetingSessionTone, string> = {
  live: 'bg-emerald-500',
  pending: 'bg-amber-500',
  fault: 'bg-red-500',
  idle: 'bg-slate-400 dark:bg-[var(--text-tertiary)]',
}

const INTERNAL_RECORD_FALLBACK_BACK_PATH = '/home'

/** React Router stores `idx` on history.state; fall back to `history.length`. */
function canNavigateBack(): boolean {
  const idx = (window.history.state as { idx?: number } | null)?.idx
  if (typeof idx === 'number') return idx > 0
  return window.history.length > 1
}

function configProblemMessage(
  problem: InternalMeetingConfigProblem,
  t: ReturnType<typeof useUiStrings>['t']
): string {
  switch (problem) {
    case 'missing_target_lang':
      return t.internalMeetingNeedsTargetLang
    case 'native_mode_not_supported':
      return t.internalMeetingNoNativeMode
    case 'stt_provider_not_supported':
      return t.internalMeetingNoSttProvider
    default:
      return t.internalMeetingFailed
  }
}

function PlayIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="currentColor" aria-hidden="true">
      <path d="M8 5.14v13.72a1 1 0 0 0 1.5.86l11.04-6.86a1 1 0 0 0 0-1.72L9.5 4.28A1 1 0 0 0 8 5.14Z" />
    </svg>
  )
}

function PauseIcon({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className={className}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" d="M9 7v10M15 7v10" />
    </svg>
  )
}

/** Idle mic glyph matching the EmptySpeakingCard accent treatment from live panes. */
function IdleMicIcon() {
  return (
    <div
      className="flex h-14 w-14 items-center justify-center rounded-2xl bg-[var(--accent-light)] text-[var(--accent)] ring-1 ring-[var(--ring)] sm:h-16 sm:w-16"
      aria-hidden
    >
      <MicIcon className="h-7 w-7 sm:h-8 sm:w-8" />
    </div>
  )
}

interface InternalMeetingRecordViewProps {
  meetingId: string
}

export default function InternalMeetingRecordView({ meetingId }: InternalMeetingRecordViewProps) {
  const { t } = useUiStrings()
  const navigate = useNavigate()
  const { isOnline } = useOnlineStatus()
  const isLgUp = useMediaQuery(MEDIA_LG_UP)

  const meeting = useStore((s) => s.getCurrentMeeting())
  const settings = useStore((s) => s.settings)
  const pendingStartMeetingId = useStore((s) => s.settings.pendingStartMeetingId)
  const clearPendingStartMeeting = useStore((s) => s.clearPendingStartMeeting)
  const deleteMeeting = useStore((s) => s.deleteMeeting)
  const addPendingSavedMeeting = useStore((s) => s.addPendingSavedMeeting)
  const removePendingSavedMeeting = useStore((s) => s.removePendingSavedMeeting)
  const showSaveToast = useStore((s) => s.showSaveToast)
  const markInternalMinutesAudioQueueFailed = useStore(
    (s) => s.markInternalMinutesAudioQueueFailed
  )

  const [captureStream, setCaptureStream] = useState<MediaStream | null>(null)
  const captureStreamRef = useRef<MediaStream | null>(null)
  const [micError, setMicError] = useState<string | null>(null)
  const [isEnding, setIsEnding] = useState(false)
  const [showOfflineEndPrompt, setShowOfflineEndPrompt] = useState(false)
  const [mobileFooterOpen, setMobileFooterOpen] = useState(true)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)
  /** Archive blob is ready but metadata/navigate failed — user can press End again. */
  const [hasStoppedArchive, setHasStoppedArchive] = useState(false)
  const recordingStartedAtRef = useRef<number | null>(null)
  /** Meeting clock that freezes while paused — source of truth for saved duration. */
  const elapsedSecondsRef = useRef(0)
  const stateRef = useRef<string>('idle')
  /** First successful archive stop; reused when End Meeting retries after `queued`. */
  const archivedAudioRef = useRef<Blob | null>(null)
  const archiveRecorderRef = useRef(new ArchiveAudioRecorder())
  const stopRecordingRef = useRef<() => Promise<void>>(async () => {})
  /** Prevents double auto-start from StrictMode / re-renders (mirror MeetingChat). */
  const autoStartAttemptRef = useRef<{ meetingId: string; pendingId: string } | null>(null)
  /** `skipped` = never committed (offline/ending); clears attempt so auto-start can retry. */
  const handleRecordMeetingRef = useRef<() => Promise<'started' | 'skipped' | 'failed'>>(
    async () => 'skipped'
  )

  const recording = useInternalMeetingRecording({
    targetLang: settings.targetLang,
    meetingId,
    userId: getCurrentUserId() ?? undefined,
    userName: getCurrentUserName() ?? undefined,
    context: settings.context,
    nativeMode: false,
    sttProvider: null,
    deliverMinutesViaClientUpload: true,
  })

  stopRecordingRef.current = recording.stop

  useEffect(() => {
    stateRef.current = recording.state
  }, [recording.state, recording.queued])

  useEffect(() => {
    elapsedSecondsRef.current = elapsedSeconds
  }, [elapsedSeconds])

  useEffect(() => {
    if (isLgUp) {
      setMobileFooterOpen(true)
    }
  }, [isLgUp])

  useEffect(() => {
    if (recording.state !== 'recording') return undefined
    // Start (or continue) the meeting clock only while audio is actually flowing —
    // not during the connect handshake after Record is pressed.
    if (recordingStartedAtRef.current === null) {
      recordingStartedAtRef.current = Date.now() - elapsedSecondsRef.current * 1000
    }
    const tick = () => {
      const startedAt = recordingStartedAtRef.current
      if (!startedAt) return
      const next = Math.max(0, Math.floor((Date.now() - startedAt) / 1000))
      elapsedSecondsRef.current = next
      setElapsedSeconds(next)
    }
    tick()
    const id = window.setInterval(tick, 1000)
    return () => window.clearInterval(id)
  }, [recording.state])

  const stopCaptureTracks = useCallback(() => {
    captureStreamRef.current?.getTracks().forEach((track) => track.stop())
    captureStreamRef.current = null
    registerInternalMeetingCaptureStream(null)
    setCaptureStream(null)
    void archiveRecorderRef.current.stop()
  }, [])

  const releaseMicTracks = useCallback(() => {
    captureStreamRef.current?.getTracks().forEach((track) => track.stop())
    captureStreamRef.current = null
    registerInternalMeetingCaptureStream(null)
    setCaptureStream(null)
  }, [])

  const isEndingRef = useRef(false)
  useEffect(() => {
    isEndingRef.current = isEnding
  }, [isEnding])

  // Leaving the page must tear down mic + socket; otherwise capture keeps running
  // with no UI. While End Meeting is in flight, leave the archive recorder alone
  // so handleEndMeeting can stop it and cache the blob for upload.
  useEffect(() => {
    return () => {
      const sessionState = stateRef.current
      if (
        sessionState === 'connecting' ||
        sessionState === 'recording' ||
        sessionState === 'paused' ||
        sessionState === 'stopping'
      ) {
        void stopRecordingRef.current()
      }
      releaseMicTracks()
      if (!isEndingRef.current) {
        void archiveRecorderRef.current.stop()
      }
    }
  }, [releaseMicTracks])

  // Release the mic on start failures only. Do not release on `queued`: End Meeting
  // must stop ArchiveAudioRecorder first; releasing tracks early races archive.stop()
  // and yields a 0-byte blob (no upload).
  useEffect(() => {
    if (recording.state !== 'error') return
    stopCaptureTracks()
  }, [recording.state, stopCaptureTracks])

  const handleRecordMeeting = useCallback(async (): Promise<'started' | 'skipped' | 'failed'> => {
    if (!isOnline || isEnding) return 'skipped'
    setMicError(null)
    stopCaptureTracks()
    const deviceId = settings.meetingSetup.microphoneDeviceId
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: deviceId ? { deviceId: { ideal: deviceId } } : true,
      })
      captureStreamRef.current = stream
      registerInternalMeetingCaptureStream(stream)
      setCaptureStream(stream)
      archiveRecorderRef.current.start(stream)
      archivedAudioRef.current = null
      setHasStoppedArchive(false)
      recordingStartedAtRef.current = null
      elapsedSecondsRef.current = 0
      setElapsedSeconds(0)
      await recording.start(stream)
      return 'started'
    } catch (error) {
      stopCaptureTracks()
      setMicError(error instanceof Error ? error.message : t.microphoneTrackMissing)
      return 'failed'
    }
  }, [
    isEnding,
    isOnline,
    recording,
    settings.meetingSetup.microphoneDeviceId,
    stopCaptureTracks,
    t.microphoneTrackMissing,
  ])

  handleRecordMeetingRef.current = handleRecordMeeting

  // Auto-start after Start Meeting navigates here with pendingStartMeetingId set.
  useEffect(() => {
    const pendingId = pendingStartMeetingId
    const alreadyAttempted =
      autoStartAttemptRef.current?.meetingId === meetingId &&
      autoStartAttemptRef.current?.pendingId === pendingId

    const shouldAutoStart =
      pendingId === meetingId &&
      !alreadyAttempted &&
      recording.state === 'idle' &&
      !isEnding &&
      isOnline

    if (!shouldAutoStart) return

    const timeoutId = window.setTimeout(() => {
      autoStartAttemptRef.current = { meetingId, pendingId }
      void handleRecordMeetingRef
        .current()
        .then((result) => {
          // Never committed (offline/ending race) — allow a later auto-start.
          if (
            result === 'skipped' &&
            autoStartAttemptRef.current?.meetingId === meetingId &&
            autoStartAttemptRef.current?.pendingId === pendingId
          ) {
            autoStartAttemptRef.current = null
          }
        })
        .catch(() => {
          /* Mic / start errors surface via micError and recording.state. */
        })
    }, 0)

    return () => window.clearTimeout(timeoutId)
  }, [isEnding, isOnline, meetingId, pendingStartMeetingId, recording.state])

  useEffect(() => {
    if (recording.state === 'recording' && pendingStartMeetingId === meetingId) {
      clearPendingStartMeeting()
    }
  }, [clearPendingStartMeeting, meetingId, pendingStartMeetingId, recording.state])

  useEffect(() => {
    if (
      (recording.state !== 'error' && !micError) ||
      pendingStartMeetingId !== meetingId
    ) {
      return
    }
    const attempt = autoStartAttemptRef.current
    if (attempt?.meetingId === meetingId && attempt.pendingId === meetingId) {
      clearPendingStartMeeting()
    }
  }, [clearPendingStartMeeting, meetingId, micError, pendingStartMeetingId, recording.state])

  const handleBack = useCallback(async () => {
    const sessionState = stateRef.current
    const leavingActiveSession =
      sessionState === 'connecting' ||
      sessionState === 'recording' ||
      sessionState === 'paused' ||
      sessionState === 'stopping'

    if (leavingActiveSession && !window.confirm(t.leaveWhileRecordingPrompt)) {
      return
    }

    if (leavingActiveSession) {
      await stopRecordingRef.current()
    }
    stopCaptureTracks()
    clearPendingStartMeeting()

    if (canNavigateBack()) {
      navigate(-1)
      return
    }
    navigate(INTERNAL_RECORD_FALLBACK_BACK_PATH)
  }, [clearPendingStartMeeting, navigate, stopCaptureTracks, t.leaveWhileRecordingPrompt])

  const finalizeAndNavigate = useCallback(
    async (archivedAudio: Blob | null) => {
      if (!meeting) throw new Error('Meeting is no longer available')

      // Prefer the pause-aware meeting clock over wall time from start.
      const durationSeconds = Math.max(1, elapsedSecondsRef.current)
      const startedAt =
        recordingStartedAtRef.current ?? Date.now() - durationSeconds * 1000

      const contextText = settings.context.text?.trim()
      const minutesContext = serializeSttConfigContextForMinutesReference(settings.context)
      await completeMeetingMetadata(meeting.id, {
        durationSeconds,
        realtimeContext: contextText || undefined,
        minutesContext,
      })

      addPendingSavedMeeting({
        clientId: meeting.id,
        title: meeting.title,
        sourceLang: meeting.sourceLang,
        targetLang: meeting.targetLang,
        durationSeconds,
        startedAt,
      })
      deleteMeeting(meeting.id)
      setHasStoppedArchive(false)
      stopCaptureTracks()
      navigate(`/meetings/${meeting.id}`, { state: { from: '/home' } })

      const audioBlob = archivedAudio
      void (async () => {
        const pendingClientId = meeting.id
        const hasArchiveAudio = Boolean(audioBlob && audioBlob.size > 0)

        // One upload, then the ask — not both at once with the same blob. The
        // upload response means the backend accepted the file, not that S3 is
        // already done; `/minutes/audio` records a durable waiting job and the
        // backend starts it after the recording reaches storage.
        const uploaded = await uploadMeetingAudioInBackground(pendingClientId, audioBlob).then(
          () => true,
          (error: unknown) => {
            console.warn('Internal meeting audio upload failed', error)
            return false
          }
        )
        const minutesQueued =
          !hasArchiveAudio || !uploaded
            ? false
            : await requestMeetingMinutesFromAudioInBackground(pendingClientId).then(
                () => true,
                (error: unknown) => {
                  console.warn('Internal meeting minutes request failed', error)
                  return false
                }
              )

        removePendingSavedMeeting(pendingClientId)

        const audioFailed = hasArchiveAudio && !uploaded
        const minutesFailed = hasArchiveAudio && uploaded && !minutesQueued

        if (audioFailed) {
          // The minutes never got a chance to be asked for, so this is the one
          // thing to report and the one thing Retry has to repair.
          markInternalMinutesAudioQueueFailed(pendingClientId)
          showSaveToast({ kind: 'error', message: t.failedUploadMeetingAudio })
        } else if (minutesFailed) {
          markInternalMinutesAudioQueueFailed(pendingClientId)
          showSaveToast({ kind: 'error', message: t.failedGenerateMeetingMinutes })
        } else {
          useStore.getState().clearInternalMinutesAudioQueueFailed(pendingClientId)
          showSaveToast({ kind: 'success', message: t.meetingSavedToast })
        }
      })().catch((error: unknown) => {
        // The row on Home is the only thing telling the reader this save is
        // still running, and nothing above is guaranteed to reach the line
        // that clears it. A throw used to leave the row behind AND go out as
        // an unhandled rejection, so nobody learned either fact.
        console.warn('Internal meeting background save threw', error)
        // `meeting.id` rather than the inner `pendingClientId`, which is scoped
        // to the promise that just threw. They are the same value.
        removePendingSavedMeeting(meeting.id)
      })
    },
    [
      addPendingSavedMeeting,
      deleteMeeting,
      meeting,
      navigate,
      settings.context,
      setHasStoppedArchive,
      markInternalMinutesAudioQueueFailed,
      removePendingSavedMeeting,
      requestMeetingMinutesFromAudioInBackground,
      showSaveToast,
      stopCaptureTracks,
      t.failedGenerateMeetingMinutes,
      t.failedUploadMeetingAudio,
      t.meetingSaveFailedToast,
      t.meetingSavedToast,
    ]
  )

  const handleTogglePause = useCallback(() => {
    if (isEnding) return
    if (recording.state === 'recording') {
      recording.pause()
      archiveRecorderRef.current.pause()
      return
    }
    if (recording.state === 'paused') {
      // Rebase so resumed ticks continue from the frozen elapsed, excluding pause.
      recordingStartedAtRef.current = Date.now() - elapsedSecondsRef.current * 1000
      recording.resume()
      archiveRecorderRef.current.resume()
    }
  }, [isEnding, recording])

  const handleEndMeeting = useCallback(async () => {
    if (!isOnline) {
      setShowOfflineEndPrompt(true)
      return
    }
    if (isEnding) return
    setIsEnding(true)
    try {
      let archivedAudio: Blob | null = archivedAudioRef.current
      const needsCaptureStop =
        stateRef.current === 'recording' ||
        stateRef.current === 'paused' ||
        stateRef.current === 'stopping'
      if (needsCaptureStop) {
        await recording.stop()
        if (stateRef.current === 'error') {
          throw recording.error ?? new Error(t.internalMeetingFailed)
        }
        archivedAudio = await archiveRecorderRef.current.stop()
        archivedAudioRef.current = archivedAudio
        setHasStoppedArchive(Boolean(archivedAudio && archivedAudio.size > 0))
      }
      await finalizeAndNavigate(archivedAudio)
    } catch (error) {
      setMicError(error instanceof Error ? error.message : t.internalMeetingFailed)
      stopCaptureTracks()
    } finally {
      setIsEnding(false)
    }
  }, [
    finalizeAndNavigate,
    isEnding,
    isOnline,
    recording,
    stopCaptureTracks,
    t.internalMeetingFailed,
  ])

  const isRecording =
    recording.state === 'recording' || recording.state === 'paused' || recording.state === 'stopping'
  const isConnecting = recording.state === 'connecting'
  /** Idle or a failed start/session — user can press Record again. */
  const showRecordControl = recording.state === 'idle' || recording.state === 'error'
  const canPressRecord = showRecordControl && !isEnding && isOnline
  const showPauseResumeControl =
    recording.state === 'recording' || recording.state === 'paused'
  const canTogglePause = showPauseResumeControl && !isEnding
  const showEndControl =
    isConnecting ||
    isRecording ||
    recording.state === 'queued' ||
    recording.state === 'stopping' ||
    hasStoppedArchive
  const hasCaptureStream = Boolean(captureStreamRef.current ?? captureStream)
  const showVisualizer =
    isRecording || isConnecting || (recording.state === 'stopping' && hasCaptureStream)
  /** Queued keeps End enabled so a failed finalize can be retried. */
  const canEndMeeting =
    (isRecording || isConnecting || recording.state === 'queued' || hasStoppedArchive) &&
    !isEnding

  const statusLabel =
    recording.state === 'recording'
      ? t.recordingStatus
      : recording.state === 'paused'
        ? t.internalMeetingPaused
        : isConnecting
          ? t.starting
          : recording.state === 'stopping' || isEnding
            ? t.saving
            : recording.state === 'error' || micError
              ? (micError ??
                recording.error?.message ??
                (recording.configProblem
                  ? configProblemMessage(recording.configProblem, t)
                  : t.statusFailed))
              : t.sessionKindInternalMeeting

  const statusTone: MeetingSessionTone =
    recording.state === 'error' || Boolean(micError)
      ? 'fault'
      : recording.state === 'recording'
        ? 'live'
        : meetingSessionStatusTone(
            recording.state === 'queued' || recording.state === 'idle'
              ? 'idle'
              : recording.state === 'connecting'
                ? 'connecting'
                : recording.state === 'paused'
                  ? 'paused'
                  : recording.state === 'stopping'
                    ? 'stopping'
                    : 'idle'
          )

  const languageLabel = getLanguageDisplayName(
    meeting?.targetLang ?? settings.targetLang,
    t
  )
  const title = meeting?.title ?? t.sessionKindInternalMeeting
  const isTimerLive = recording.state === 'recording'

  const errorMessage =
    micError ??
    recording.error?.message ??
    (recording.configProblem ? configProblemMessage(recording.configProblem, t) : null)

  return (
    <div className="flex h-full min-h-0 flex-col bg-white text-slate-950 dark:bg-[var(--bg-secondary)] dark:text-[var(--text-primary)]">
      <header className="flex min-h-0 shrink-0 flex-nowrap items-center gap-2 border-b border-blue-100 bg-white px-3 py-2 dark:border-[var(--border-color)] dark:bg-[var(--bg-secondary)] sm:min-h-[72px] sm:flex-wrap sm:gap-3 sm:px-6 sm:py-3">
        <button
          type="button"
          onClick={() => void handleBack()}
          data-testid="internal-record-back"
          className="flex h-9 w-9 shrink-0 items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 transition-colors hover:bg-slate-50 dark:border-[var(--border-color)] dark:bg-[var(--bg-primary)] dark:text-[var(--text-secondary)] dark:hover:bg-[var(--bg-tertiary)]"
          aria-label={t.backToMeetings}
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </button>
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-nowrap items-center gap-2 sm:gap-3">
            <h1
              title={title}
              className="min-w-0 truncate text-[11px] font-bold text-slate-950 dark:text-[var(--text-primary)] sm:text-sm"
            >
              {title}
            </h1>
            <span
              data-testid="meeting-language-badge"
              className="inline-flex max-w-full shrink-0 items-center gap-1 rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] font-semibold text-slate-700 dark:bg-[var(--bg-tertiary)] dark:text-[var(--text-secondary)] sm:gap-2 sm:px-3 sm:py-1 sm:text-xs"
            >
              <span className="truncate">{languageLabel}</span>
            </span>
          </div>
        </div>

        <div className="ml-auto flex shrink-0 flex-row items-center gap-1.5 sm:gap-3">
          <div
            data-testid="meeting-status-pill"
            className={[
              'inline-flex max-w-[28vw] items-center gap-1 rounded-lg border px-1.5 py-0.5 text-[11px] font-semibold sm:max-w-[40vw] sm:gap-1.5 sm:rounded-xl sm:px-4 sm:py-2 sm:text-sm',
              STATUS_PILL_CLASS[statusTone],
            ].join(' ')}
          >
            <span
              className={`h-1.5 w-1.5 shrink-0 rounded-full sm:h-2 sm:w-2 ${STATUS_DOT_CLASS[statusTone]}`}
            />
            <span className="truncate" title={statusLabel}>
              {statusLabel}
            </span>
          </div>

          <div
            data-testid="meeting-elapsed-timer"
            className={[
              'flex items-center gap-1.5 font-mono text-sm font-bold sm:gap-2 sm:text-xl',
              isTimerLive ? 'text-red-700' : 'text-slate-400 dark:text-[var(--text-tertiary)]',
            ].join(' ')}
          >
            <span
              className={
                isTimerLive
                  ? 'h-2 w-2 rounded-full bg-red-700'
                  : 'h-2 w-2 rounded-full bg-slate-300 dark:bg-[var(--text-tertiary)]'
              }
            />
            {formatElapsedHms(elapsedSeconds)}
          </div>
        </div>
      </header>

      {!isOnline && (
        <div
          role="status"
          aria-live="polite"
          className="m-3 animate-fade-in rounded-xl border border-amber-300 bg-amber-50 px-3 py-2 dark:border-amber-500/30 dark:bg-amber-500/10 sm:mx-6 sm:px-4 sm:py-3"
        >
          <p className="text-xs font-semibold text-amber-900 dark:text-amber-300 sm:text-sm">
            {t.offlineWarningTitle}
          </p>
          <p className="mt-1 text-xs leading-relaxed text-amber-800 dark:text-amber-200/80">
            {t.offlineWarningBody}
          </p>
        </div>
      )}

      <div
        className="relative flex min-h-0 flex-1 flex-col overflow-hidden"
        data-testid="meeting-main-area"
      >
        <div className="flex min-h-0 flex-1 flex-col items-center justify-center px-6 py-8">
          <div
            data-testid="internal-record-hero"
            className="flex w-full max-w-md flex-col items-center"
          >
            {showVisualizer ? (
              <div data-testid="internal-record-waves" className="w-full">
                <LiveAudioLevelBars
                  streamRef={captureStreamRef}
                  stream={captureStream}
                  active={showVisualizer}
                  reactive
                  reactiveFlatten={false}
                  silentOpacity={0.55}
                />
              </div>
            ) : (
              <div data-testid="internal-record-mic">
                <IdleMicIcon />
              </div>
            )}
          </div>

          {errorMessage ? (
            <p className="mt-6 max-w-md text-center text-sm text-[var(--error)]" role="alert">
              {errorMessage}
            </p>
          ) : null}
        </div>

        {!isLgUp && !mobileFooterOpen ? (
          <ChromeRestoreStrip
            onShow={() => setMobileFooterOpen(true)}
            showLabel={t.showMeetingFooter}
            testId="restore-meeting-footer"
          />
        ) : null}

        <CollapsibleChrome open={isLgUp || mobileFooterOpen} testId="meeting-bottom-chrome">
          <div className="relative shrink-0 border-t border-blue-100 bg-white px-3 py-2 pb-[max(0.75rem,env(safe-area-inset-bottom))] dark:border-[var(--border-color)] dark:bg-[var(--bg-secondary)] sm:px-6 sm:py-3">
            <div className="mb-1 flex justify-end lg:hidden">
              <ChromeHideButton
                onHide={() => setMobileFooterOpen(false)}
                hideLabel={t.hideMeetingFooter}
                hideIconDirection="down"
                testId="hide-meeting-footer"
              />
            </div>
            <div
              className="mx-auto flex w-full max-w-2xl flex-nowrap items-center justify-center gap-3 rounded-2xl border border-blue-100 bg-white px-3 py-2 shadow-lg shadow-blue-100/60 dark:border-[var(--border-color)] dark:bg-[var(--bg-tertiary)] dark:shadow-none sm:gap-6 sm:px-5 sm:py-3"
              data-testid="meeting-control-bar"
            >
              <div
                className="flex flex-col items-center gap-1"
                data-testid="internal-record-transport"
              >
                {showRecordControl ? (
                  <>
                    <button
                      type="button"
                      onClick={() => void handleRecordMeeting()}
                      disabled={!canPressRecord}
                      data-testid="internal-record-meeting"
                      className="flex h-11 w-11 touch-manipulation items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 transition-colors hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-emerald-500/15 dark:text-emerald-300 dark:hover:bg-emerald-500/25"
                      title={t.recordMeeting}
                      aria-label={t.recordMeeting}
                    >
                      <MicIcon className="h-4 w-4" />
                    </button>
                    <span className="hidden text-[11px] font-semibold text-slate-500 dark:text-[var(--text-tertiary)] sm:block">
                      {t.recordMeeting}
                    </span>
                  </>
                ) : showPauseResumeControl ? (
                  <>
                    <button
                      type="button"
                      onClick={handleTogglePause}
                      disabled={!canTogglePause}
                      data-testid="internal-pause-resume-meeting"
                      className="flex h-11 w-11 touch-manipulation items-center justify-center rounded-xl bg-amber-50 text-amber-600 transition-colors hover:bg-amber-100 disabled:cursor-not-allowed disabled:opacity-40 dark:bg-amber-500/15 dark:text-amber-300 dark:hover:bg-amber-500/25"
                      title={recording.state === 'paused' ? t.resume : t.pause}
                      aria-label={recording.state === 'paused' ? t.resume : t.pause}
                    >
                      {recording.state === 'paused' ? (
                        <PlayIcon className="h-4 w-4" />
                      ) : (
                        <PauseIcon className="h-4 w-4" />
                      )}
                    </button>
                    <span className="hidden text-[11px] font-semibold text-slate-500 dark:text-[var(--text-tertiary)] sm:block">
                      {recording.state === 'paused' ? t.resume : t.pause}
                    </span>
                  </>
                ) : isConnecting ? (
                  <>
                    <button
                      type="button"
                      disabled
                      data-testid="internal-record-meeting"
                      className="flex h-11 w-11 touch-manipulation items-center justify-center rounded-xl bg-emerald-50 text-emerald-600 opacity-40 dark:bg-emerald-500/15 dark:text-emerald-300"
                      title={t.starting}
                      aria-label={t.starting}
                    >
                      <MicIcon className="h-4 w-4" />
                    </button>
                    <span className="hidden text-[11px] font-semibold text-slate-500 dark:text-[var(--text-tertiary)] sm:block">
                      {t.starting}
                    </span>
                  </>
                ) : null}
              </div>

              {showEndControl ? (
                <div className="flex flex-col items-center gap-1" data-testid="internal-end-region">
                  <button
                    type="button"
                    onClick={() => {
                      if (!isOnline) {
                        setShowOfflineEndPrompt(true)
                        return
                      }
                      void handleEndMeeting()
                    }}
                    disabled={!canEndMeeting || !isOnline}
                    aria-disabled={!canEndMeeting || !isOnline}
                    data-testid="internal-end-meeting"
                    className="flex h-11 w-11 touch-manipulation items-center justify-center rounded-xl bg-red-600 text-white transition-colors hover:bg-red-700 disabled:opacity-40"
                    title={!isOnline ? t.offlineEndDisabledTooltip : t.endMeeting}
                  >
                    <StopIcon className="h-5 w-5" />
                  </button>
                  <span className="hidden text-[11px] font-semibold text-red-600 sm:block">
                    {isEnding || recording.state === 'stopping' ? t.saving : t.endMeeting}
                  </span>
                </div>
              ) : null}
            </div>
          </div>
        </CollapsibleChrome>
      </div>

      {showOfflineEndPrompt ? (
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="offline-end-prompt-title"
          className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/40 px-4"
        >
          <div className="w-full max-w-md rounded-2xl bg-white p-4 shadow-2xl dark:bg-[var(--bg-secondary)] sm:p-6">
            <p
              id="offline-end-prompt-title"
              className="text-base font-bold text-slate-950 dark:text-[var(--text-primary)] sm:text-lg"
            >
              {t.offlineCannotEndTitle}
            </p>
            <p className="mt-2 text-xs leading-relaxed text-slate-600 dark:text-[var(--text-secondary)] sm:text-sm">
              {t.offlineCannotEndBody}
            </p>
            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={() => setShowOfflineEndPrompt(false)}
                className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-semibold text-white transition-colors hover:bg-blue-700 dark:bg-[var(--accent)] dark:hover:bg-[var(--accent-hover)]"
              >
                {t.close}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  )
}
